import {
  Input,
  Tab,
  TabList,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import type { AdminUser, FacilityRow } from '@/api/types';
import {
  DataTable,
  EmptyState,
  type DataTableColumn,
  ErrorState,
  FreshnessChip,
  KeyValue,
  KpiTile,
  LoadingState,
  SectionCard,
} from '@/design-system';
import type { DirectoryUserWithRoles } from '@/models';
import { spacing } from '@/theme/tokens';

import { useOrg, useOrgUsers } from '../api';
import { IdentifierList } from '../components/IdentifierList';
import { identifierDisplayValue } from '../components/identifierDisplayValue';
import { StatusBadge } from '../components/StatusBadge';

import { buildPathWithMergedSearch } from '../navigation';

import { formatAddress, formatDateTime, maxRolledUpAtMs } from './detailPageUtils';

type OrgDetailTab = 'overview' | 'users' | 'facilities';
const TAB_VALUES: readonly OrgDetailTab[] = ['overview', 'users', 'facilities'];

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.l,
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    flexWrap: 'wrap',
  },
  breadcrumbLink: {
    color: tokens.colorBrandForegroundLink,
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusSmall,
    },
  },
  breadcrumbCurrent: {
    color: tokens.colorNeutralForeground3,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.l,
    flexWrap: 'wrap',
    paddingBottom: spacing.l,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.s,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.s,
    flexWrap: 'wrap',
  },
  title: {
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: spacing.m,
  },
  tabPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.l,
  },
  overviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: spacing.l,
  },
  kvGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: spacing.m,
  },

  searchInput: {
    maxWidth: '320px',
  },
  rowLink: {
    color: tokens.colorBrandForegroundLink,
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusSmall,
    },
  },
  sectionText: {
    color: tokens.colorNeutralForeground3,
  },
});

function isOrgDetailTab(value: unknown): value is OrgDetailTab {
  return typeof value === 'string' && (TAB_VALUES as readonly string[]).includes(value);
}

function UsersTable({ users, hasQuery, endpointUnavailable, partial }: { users: readonly DirectoryUserWithRoles[]; hasQuery: boolean; endpointUnavailable: boolean; partial: boolean }) {
  if (endpointUnavailable) return <EmptyState title="Users unavailable" description="This environment has not shipped the organization users endpoint yet." />;
  const columns: readonly DataTableColumn<DirectoryUserWithRoles>[] = [
    { id: 'name', header: 'Name', getValue: (item) => item.displayName, renderCell: (item) => item.displayName, sortable: !partial },
    { id: 'email', header: 'Email', getValue: (item) => item.emailAddress, renderCell: (item) => item.emailAddress, width: 280, sortable: !partial },
    { id: 'status', header: 'Status', getValue: (item) => item.status, renderCell: (item) => <StatusBadge status={item.status} />, sortable: !partial },
    { id: 'roles', header: 'Roles', getValue: (item) => identifierDisplayValue(item.roleReferences, item.roles), renderCell: (item) => <IdentifierList references={item.roleReferences} fallbackIds={item.roles} />, width: 260, sortable: !partial },
    { id: 'facilities', header: 'Assigned facilities', getValue: (item) => identifierDisplayValue(item.assignedFacilityReferences, item.assignedFacilities), renderCell: (item) => <IdentifierList references={item.assignedFacilityReferences} fallbackIds={item.assignedFacilities} />, width: 260, sortable: !partial },
  ];
  return <DataTable ariaLabel="Organization users" columns={columns} getRowId={(item) => item.userId} items={users} tableId="organization-users" sortDescription={partial ? 'User order follows the server order because this is a partial result.' : undefined} emptyState={<EmptyState title={hasQuery ? 'No users match the filter' : 'No users found'} description={hasQuery ? 'Try a shorter or different search query.' : "This organization doesn't have any users to show right now."} />} />;
}

function AdminsTable({ admins }: { admins: readonly AdminUser[] }) {
  const columns: readonly DataTableColumn<AdminUser>[] = [
    { id: 'name', header: 'Name', getValue: (item) => item.displayName, renderCell: (item) => item.displayName },
    { id: 'email', header: 'Email', getValue: (item) => item.email, renderCell: (item) => item.email, width: 280 },
    { id: 'status', header: 'Status', getValue: (item) => item.status, renderCell: (item) => <StatusBadge status={item.status} /> },
    { id: 'lastSignIn', header: 'Last sign-in', getValue: (item) => (item.lastSignInEpochMs ?? 0) > 0 ? item.lastSignInEpochMs : null, renderCell: (item) => formatDateTime(item.lastSignInEpochMs) },
  ];
  return <DataTable ariaLabel="Organization admins" columns={columns} getRowId={(item) => item.userId} items={admins} tableId="organization-admins" emptyState={<EmptyState title="No admins found" description="This organization doesn't have any admin assignments yet." />} />;
}

function FacilitiesTable({ facilities, currentSearch, partial }: { facilities: readonly FacilityRow[]; currentSearch: string; partial: boolean }) {
  const classes = useStyles();
  const columns: readonly DataTableColumn<FacilityRow>[] = [
    { id: 'name', header: 'Name', getValue: (item) => item.displayName, renderCell: (item) => <Link className={classes.rowLink} to={buildPathWithMergedSearch(`/business/facilities/${encodeURIComponent(item.facilityId)}`, currentSearch, { orgId: item.organizationId })}>{item.displayName}</Link>, width: 240, sortable: !partial },
    { id: 'id', header: 'ID', getValue: (item) => item.facilityId, renderCell: (item) => item.facilityId, width: 200, sortable: !partial },
    { id: 'city', header: 'City', getValue: (item) => item.city, renderCell: (item) => item.city ?? '—', sortable: !partial },
    { id: 'state', header: 'State', getValue: (item) => item.state, renderCell: (item) => item.state ?? '—', sortable: !partial },
    { id: 'activeUsers', header: 'Active users', getValue: (item) => item.activeUserCount, renderCell: (item) => item.activeUserCount?.toLocaleString() ?? '—', sortable: !partial },
  ];
  return <DataTable ariaLabel="Organization facilities" columns={columns} getRowId={(item) => item.facilityId} items={facilities} tableId="organization-facilities" sortDescription={partial ? 'Facility order follows the server order because this is a partial result.' : undefined} emptyState={<EmptyState title="No facilities found" description="This organization doesn't have any facilities in the current directory." />} />;
}

function OrgUsersPanel({
  orgUsers,
  isLoading,
  errorMessage,
  onRetry,
  userSearch,
  onUserSearchChange,
  isCapped,
  endpointUnavailable,
}: {
  orgUsers: readonly DirectoryUserWithRoles[];
  isLoading: boolean;
  errorMessage?: string;
  onRetry: () => void;
  userSearch: string;
  onUserSearchChange: (value: string) => void;
  isCapped: boolean;
  endpointUnavailable: boolean;
}) {
  const classes = useStyles();

  return (
    <SectionCard
      title="Users"
      description="Users and role assignments for this organization."
      trailing={
        <Input
          className={classes.searchInput}
          contentBefore={<SearchRegular />}
          placeholder="Search users"
          value={userSearch}
          onChange={(_, data) => {
            onUserSearchChange(data.value);
          }}
          aria-label="Search organization users"
        />
      }
    >
      {isLoading ? <LoadingState label="Loading organization users…" /> : null}
      {!isLoading && errorMessage ? (
        <ErrorState
          title="Couldn't load organization users"
          description={errorMessage}
          retry={onRetry}
        />
      ) : null}
      {!isLoading && !errorMessage ? (
        endpointUnavailable
          ? <UsersTable users={orgUsers} hasQuery={userSearch.trim().length > 0} endpointUnavailable={endpointUnavailable} partial={isCapped} />
          : <>
              <Text size={200} className={classes.sectionText}>
                {isCapped
                  ? `Showing the first ${orgUsers.length.toLocaleString()} users. Refine the search to narrow the list.`
                  : `Showing ${orgUsers.length.toLocaleString()} users`}
              </Text>
              <UsersTable users={orgUsers} hasQuery={userSearch.trim().length > 0} endpointUnavailable={endpointUnavailable} partial={isCapped} />
            </>
      ) : null}
    </SectionCard>
  );
}

export function OrgDetailPage() {
  const classes = useStyles();
  const location = useLocation();
  const { orgId: orgIdParam } = useParams<{ orgId: string }>();
  const orgId = orgIdParam ?? null;
  const [selectedTab, setSelectedTab] = useState<OrgDetailTab>('overview');
  const [userSearch, setUserSearch] = useState('');

  const orgQuery = useOrg(orgId);
  const orgUsersQuery = useOrgUsers(orgId, userSearch, selectedTab === 'users');
  const orgUsers = useMemo(() => orgUsersQuery.data?.items ?? [], [orgUsersQuery.data]);

  if (orgQuery.isLoading) {
    return <LoadingState label="Loading organization details…" />;
  }

  if (orgQuery.error) {
    return (
      <ErrorState
        title="Couldn't load organization"
        description={orgQuery.error.message}
        retry={() => {
          void orgQuery.refetch();
        }}
      />
    );
  }

  if (!orgQuery.data) {
    return <ErrorState title="Organization not found" description="Try returning to the business directory and selecting the organization again." />;
  }

  const detail = orgQuery.data;
  // dataAsOfMs is the age of the oldest input behind the response, so the chip states how
  // old the numbers are rather than when the page was fetched.
  const freshnessMs = detail.dataAsOfMs || maxRolledUpAtMs(detail.countsRolledUpAtMs);
  const orgFacilities = detail.facilities;

  return (
    <div className={classes.root}>
      <nav className={classes.breadcrumb} aria-label="Breadcrumb">
        <Link
          to={buildPathWithMergedSearch('/business', location.search)}
          className={classes.breadcrumbLink}
        >
          Business
        </Link>
        <Text size={200} className={classes.breadcrumbCurrent} aria-hidden="true">›</Text>
        <Text size={200} className={classes.breadcrumbCurrent}>Organizations</Text>
        <Text size={200} className={classes.breadcrumbCurrent} aria-hidden="true">›</Text>
        <Text size={200} className={classes.breadcrumbCurrent} aria-current="page">{detail.displayName}</Text>
      </nav>

      <div className={classes.header}>
        <div className={classes.headerText}>
          <div className={classes.titleRow}>
            <Text size={700} weight="semibold" className={classes.title}>
              {detail.displayName}
            </Text>
          </div>
          <Text size={300} className={classes.subtitle}>
            {formatAddress(detail.addresses)}
          </Text>
        </div>
        {freshnessMs ? (
          <FreshnessChip
            asOfMs={freshnessMs}
            isRefreshing={orgQuery.isFetching || orgUsersQuery.isFetching}
            onRefresh={() => {
              void orgQuery.refetch();
              if (selectedTab === 'users') {
                void orgUsersQuery.refetch();
              }
            }}
          />
        ) : null}
      </div>

      <div className={classes.kpiGrid}>
        <KpiTile label="Facilities" value={detail.counts.facilitiesTotal} unit="count" />
        <KpiTile label="Total users" value={detail.counts.usersTotal} unit="count" />
        <KpiTile
          label="Active users"
          value={detail.counts.usersActive}
          unit="count"
        />
        <KpiTile label="Docs signed (last 7 days)" value={detail.counts.docsSignedLast7d} unit="count" />
      </div>

      <TabList
        selectedValue={selectedTab}
        onTabSelect={(_, data) => {
          if (isOrgDetailTab(data.value)) {
            setSelectedTab(data.value);
          }
        }}
        aria-label="Organization detail tabs"
      >
        <Tab value="overview">Overview</Tab>
        <Tab value="users">Users</Tab>
        <Tab value="facilities">Facilities</Tab>
      </TabList>

      {selectedTab === 'overview' ? (
        <div className={classes.tabPanel}>
          <div className={classes.overviewGrid}>
            <SectionCard title="Contacts">
              <div className={classes.kvGrid}>
                <KeyValue label="Primary contact">{detail.contact.name ?? '—'}</KeyValue>
                <KeyValue label="Email">{detail.contact.email ?? '—'}</KeyValue>
                <KeyValue label="Phone">{detail.contact.phone ?? '—'}</KeyValue>
                <KeyValue label="Address">{formatAddress(detail.addresses)}</KeyValue>
              </div>
            </SectionCard>
            <SectionCard title="Key dates">
              <div className={classes.kvGrid}>
                <KeyValue label="Created">{formatDateTime(detail.createdAtEpochMs)}</KeyValue>
                <KeyValue label="License type">{detail.license?.type ?? '—'}</KeyValue>
                <KeyValue label="License expires">{formatDateTime(detail.license?.validThroughEpochMs)}</KeyValue>
                <KeyValue label="Facilities loaded">
                  {detail.facilitiesContinuation ? 'Partial list in overview payload' : 'All current facilities'}
                </KeyValue>
              </div>
            </SectionCard>
          </div>
          <SectionCard title="Admins" description="Organization-level admins and recent sign-in activity.">
            <AdminsTable admins={detail.admins} />
          </SectionCard>
        </div>
      ) : null}

      {selectedTab === 'users' ? (
        <div className={classes.tabPanel}>
          <OrgUsersPanel
            orgUsers={orgUsers}
            isLoading={orgUsersQuery.isLoading}
            errorMessage={orgUsersQuery.error?.message}
            onRetry={() => {
              void orgUsersQuery.refetch();
            }}
            userSearch={userSearch}
            onUserSearchChange={setUserSearch}
            isCapped={orgUsersQuery.data?.isCapped ?? false}
            endpointUnavailable={orgUsersQuery.data?.endpointUnavailable ?? false}
          />
        </div>
      ) : null}

      {selectedTab === 'facilities' ? (
        <div className={classes.tabPanel}>
          <SectionCard
            title="Facilities"
            description={detail.facilitiesContinuation
              ? 'Showing the facilities currently included in the organization detail payload.'
              : 'Facilities in this organization.'}
          >
            <FacilitiesTable facilities={orgFacilities} currentSearch={location.search} partial={Boolean(detail.facilitiesContinuation)} />
          </SectionCard>
        </div>
      ) : null}
    </div>
  );
}
